import { Component } from "react";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import axios from "axios";

import { appInsights } from "../utils/logHandlers/appInsight";
import { ErrorResponse, getSessionStorage, handleUnauthorizedError, isNil, isUnauthorizedSession } from "../utils";
import SnackbarUtils from "../utils/SnackbarProvider";
import { userDate, utcDateToUser } from "../utils/DateTime";

type Methods = "head" | "options" | "put" | "post" | "patch" | "delete" | "get";

class API extends Component {
    static call<T>(method: Methods, url: string, type: T, ...rest: any[]) {
        const [payload] = rest;
        const req = userDate(rest);
        return axios[method](url, ...req)
            .then((response) => {
                if (type && type == "file") {
                    const responseData = utcDateToUser(response);
                    return responseData;
                }
                if (type === "getError") {
                    const responseData = utcDateToUser(response);
                    return responseData;
                } else {
                    const responseData = utcDateToUser(response.data);
                    return responseData;
                }
            })
            .catch((err) => {
                if (handleUnauthorizedError(err)) {
                  return;
                }
              
                if (type && type == "file") {
                  return err;
                }
              
                if (err.response?.status === 404) {
                  return null;
                }
              
                if (type === "getError") {
                  const responseData = utcDateToUser(err.response);
                  return responseData;
                }
              
                if (err.response?.status) {
                  if (appInsights !== undefined) {
                    appInsights.trackException({
                      error: err.response.status,
                      severityLevel: SeverityLevel.Error,
                      properties: { payload },
                    });
                  }
                }
              
                SnackbarUtils.warning("Something went wrong!");
              });
    }

    static callAPI<T>(method: Methods, url: string, ...rest: T[]) {
        const [payload] = rest;
        const req = userDate(rest);
        return axios[method](url, ...req)
            .then((response) => {
                const responseData = utcDateToUser(response);
                return responseData;
            })
            .catch((err) => {
                if (handleUnauthorizedError(err)) {
                  return;
                }
              
                if (err.response?.status) {
                  if (appInsights !== undefined) {
                    appInsights.trackException({
                      error: err.response.status,
                      severityLevel: SeverityLevel.Error,
                      properties: { payload },
                    });
                  }
                }
              
                return ErrorResponse(err.response);
              });
    }

    static getBlob(method: Methods, url: string, header?: { headers?: { Authorization?: string } }) {
        return axios({
            url: url,
            method: method,
            headers: header?.headers,
            responseType: "blob",
        })
            .then((response) => {
                return response;
            })
            .catch((err) => {
                if (handleUnauthorizedError(err)) {
                  return;
                }
              
                if (err.response?.status) {
                  if (appInsights !== undefined) {
                    appInsights.trackException({
                      error: err.response.status,
                      severityLevel: SeverityLevel.Error,
                    });
                  }
                }
              
                return ErrorResponse(err.response);
              });
    }

    //-------------------------------------------
    //For token system
    static userId: number;
    static getRootUrl(url: string) {
        return url.toString();
    }

    static setUserId(userId: number) {
        this.userId = userId;
    }
    static getUserId() {
        return this.userId;
    }

    static APIResponse<T>(
        method: Methods,
        url: string,
        data?: T,
        isPostPut?: boolean,
        token?: string,
        skipHeader?: boolean
    ) {
        const TempToken = getSessionStorage("samlToken");

        const header = skipHeader ? {} : {
            headers: {
                Authorization: `Bearer ${!isNil(TempToken) && TempToken.length > 0 ? TempToken : token}`,
            },
        };
        const fileType = data;
        if (fileType && (fileType == "excel" || fileType == "file")) {
            return API.getBlob(method, url, header);
        } else if (isPostPut) {
            return API.call(method, url, "", data, header);
        } else if (method == "get" && data === true) {
            return API.callAPI(method, url, header);
        } else if (method == "get" || method == "delete") {
            return API.call(method, url, data, header);
        } else {
            return API.callAPI(method, url, data);
        }
    }
    //-----------------------------------------------
    static APICall<T>(
        method: Methods,
        url: string,
        data?: T,
        isPostPut?: boolean,
        skipHeader?: boolean
    ) {
        if (!skipHeader && isUnauthorizedSession()) return Promise.resolve();

        const TempToken = getSessionStorage("samlToken");

        return API.APIResponse(
            method,
            url,
            data,
            isPostPut,
            TempToken,
            skipHeader
        );
    }

    static getApi(url: string, config?: string) {
        return API.call("get", url, config);
    }

    static get<T>(url: string, type?: T) {
        return API.APICall("get", url, type);
    }
    static getApiError(url: string) {
        return API.APICall("get", url, "getError");
    }

    static post<T>(url: string, data?: T) {
        return API.APICall("post", url, data, true);
    }
    static put(url: string, data: any) {
        return API.APICall("put", url, data, true);
    }
    static postApi<T>(url: string, data: T, skipHeader?: boolean) {
        return API.APICall("post", url, data, false, skipHeader);
    }
}
export default API;